import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/authService';
import { User } from '../services/apiService'; // Importáljuk a User interfészt

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  // Javítás: user állapot típusának megváltoztatása User | null-ra
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Inicializáláskor ellenőrizzük a bejelentkezési állapotot
// src/contexts/AuthContext.tsx
useEffect(() => {
  const checkAuth = async () => {
    setLoading(true);
    const isLoggedIn = authService.isAuthenticated();
    // Olvassuk ki a force_logout paramétert
    const params = new URLSearchParams(window.location.search);
    const isForcedLogout = params.get('force_logout') === 'true';
    
    if (isLoggedIn && !isForcedLogout) {
      try {
        // Felhasználói adatok betöltése
        const userData = authService.getCurrentUser() as User;
        setUser(userData);
        setIsAuthenticated(true);
        
        // App token generálása - ezt hozzáadjuk, hogy mindig legyen érvényes token
        try {
          // >>> MÓDOSÍTÁS: Ezt a blokkot csak akkor futtatjuk, ha !isForcedLogout <<<
          // (A külső if már biztosítja ezt, de a biztonság kedvéért itt is maradhat a logika)
          console.log("Auth check passed, attempting to generate app token (not forced logout).");
        //  await authService.generateAppToken();
          console.log("App token successfully generated on auth check");

        } catch (tokenErr) {
          console.warn("Could not generate app token:", tokenErr);
        }
        
        // Nem admin és van alapértelmezett app esetén átirányítás
        if (userData.role !== 'admin' && userData.default_app && window.location.pathname === '/') {
          let redirectUrl = userData.default_app;
          if (!redirectUrl.endsWith('/')) {
            redirectUrl += '/';
          }
          console.log(`Redirecting to default app: ${redirectUrl}`);
          window.location.href = redirectUrl;
          // return;
        }
      } catch (err) {
        console.error('User data loading error:', err);
        authService.logout();
        setIsAuthenticated(false);
        setUser(null);
      }
    } else {
      if (isForcedLogout) {
        console.log("Forced logout detected. Ensuring local state is cleared and skipping auth recovery.");
        // Itt expliciten is törölhetjük a localstorage-t, bár a CraneFlow már megtette

        // De ez biztosítja, hogy a TestPulse saját állapota is tiszta legyen
        authService.logout(); // Ez csak a localStorage-t törli
    } else {
        console.log("User is not logged in.");
    }
    setIsAuthenticated(false);
    setUser(null);
  }

  setLoading(false);
};

checkAuth();

  const handleStorageChange = () => {
    checkAuth();
  };

  window.addEventListener('storage', handleStorageChange);

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkAuth();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  let tokenRefreshTimer: NodeJS.Timeout | null = null;
  
  if (isAuthenticated) {
    // Token frissítés 20 percenként
    tokenRefreshTimer = setInterval(async () => {
      try {
        // JWT token frissítése
        await authService.refreshTokenIfNeeded();
        
        // App token frissítése 
        await authService.generateAppToken();
        console.log("Tokens automatically refreshed");
      } catch (err) {
        console.error("Error during automatic token refresh:", err);
      }
    }, 20 * 60 * 1000); // 20 perc
  }
  
  // Időzítő törlése kilépéskor
  return () => {
    if (tokenRefreshTimer) {
      clearInterval(tokenRefreshTimer);
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('storage', handleStorageChange);
  };
}, [isAuthenticated]); // Csak akkor fusson újra, ha a bejelentkezési állapot változik

  const login = () => {
    setIsAuthenticated(true);
    // Típuskonverzió: a getCurrentUser() visszatérési értékét User típusként kezeljük
    setUser(authService.getCurrentUser() as User);
  };

  const logout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};